/**
 * Thema (spec 10.2): the cards with their images, the five questions with the correct option
 * marked, and the source record — site, title, permanent revision link, retrieval date, licence
 * and the full source text collapsed. The source text is teacher-only (CONTEXT.md "Source").
 *
 * A «Melden» button sits on every card and every question (spec 10.3). Flags go to the operator,
 * who rules on them with `scrolivo flags resolve`; the teacher sees the ruling under "Meine
 * Meldungen". While a flag is open the topic stays live.
 */

import { useState } from "react";
import { Link, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authedJson } from "../shared/api.js";
import type { TopicCard, TopicDetail, TopicQuestion } from "../shared/api-types.js";
import { germanMessage } from "../shared/errors.js";
import { ExternalLink } from "../shared/platform.js";
import { FLAG_REASONS, FLAG_REASON_LABELS, type FlagReason } from "./labels.js";
import { Dialog, ErrorLine, Field, Loading, Panel, TButton, formatDate, inputClass } from "./ui.js";

const MAX_COMMENT = 500;

type FlagTarget =
  { kind: "card"; id: string; label: string } | { kind: "question"; id: string; label: string };

export function TopicScreen() {
  const { slug = "" } = useParams();
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<FlagTarget | null>(null);
  const [sent, setSent] = useState<string | null>(null);

  const topic = useQuery({
    queryKey: ["teacher", "topic", slug],
    queryFn: () => authedJson<TopicDetail>("/teacher/topics/" + slug, { subjectType: "teacher" }),
  });

  const flag = useMutation({
    mutationFn: (input: { reason: FlagReason; comment: string }) =>
      authedJson("/teacher/flags", {
        method: "POST",
        subjectType: "teacher",
        body: JSON.stringify({
          topicId: topic.data?.id,
          ...(target?.kind === "card" ? { cardId: target.id } : {}),
          ...(target?.kind === "question" ? { questionId: target.id } : {}),
          reason: input.reason,
          ...(input.comment.trim() ? { comment: input.comment.trim() } : {}),
        }),
      }),
    onSuccess: async () => {
      setSent("Danke, die Meldung ist bei Scrolivo angekommen.");
      setTarget(null);
      await queryClient.invalidateQueries({ queryKey: ["teacher", "flags"] });
    },
  });

  if (topic.isPending) return <Loading />;
  if (topic.isError || !topic.data) return <ErrorLine>{germanMessage(topic.error)}</ErrorLine>;

  const data = topic.data;

  return (
    <>
      <nav className="mb-3 text-[14px]">
        <Link
          to="/lehrer/themen"
          className="text-[var(--color-accent)] no-underline hover:underline"
        >
          Themen
        </Link>
        <span className="text-[var(--color-muted)]"> / {data.title}</span>
      </nav>
      <div className="mb-5 flex flex-wrap items-baseline gap-3">
        <h1 className="m-0 text-[26px] font-bold">{data.title}</h1>
        <span className="text-[15px] text-[var(--color-muted)]">
          {data.era} · veröffentlicht {formatDate(data.publishedAt)}
          {data.rewrittenAt ? " · umgeschrieben " + formatDate(data.rewrittenAt) : ""}
        </span>
      </div>

      {sent && (
        <p
          role="status"
          className="mb-4 rounded-lg border border-[#bbf7d0] bg-[#f0fdf4] px-4 py-3 text-[15px] text-[var(--color-ok)]"
        >
          {sent}
        </p>
      )}

      <div className="flex flex-col gap-5">
        <Panel title={data.cards.length + " Karten"}>
          <ol className="m-0 flex list-none flex-col gap-0 p-0">
            {data.cards.map((card) => (
              <CardRow
                key={card.id}
                card={card}
                onFlag={() =>
                  setTarget({ kind: "card", id: card.id, label: "Karte " + card.position })
                }
              />
            ))}
          </ol>
        </Panel>

        <Panel title="Die fünf Fragen">
          <ol className="m-0 flex list-none flex-col gap-0 p-0">
            {data.questions.map((question) => (
              <QuestionRow
                key={question.id}
                question={question}
                onFlag={() =>
                  setTarget({
                    kind: "question",
                    id: question.id,
                    label: "Frage " + question.position,
                  })
                }
              />
            ))}
          </ol>
        </Panel>

        <SourcePanel source={data.source} />
      </div>

      {target && (
        <FlagDialog
          target={target}
          pending={flag.isPending}
          error={flag.isError ? germanMessage(flag.error) : null}
          onSubmit={(reason, comment) => flag.mutate({ reason, comment })}
          onClose={() => {
            setTarget(null);
            flag.reset();
          }}
        />
      )}
    </>
  );
}

function CardRow({ card, onFlag }: { card: TopicCard; onFlag: () => void }) {
  return (
    <li className="flex flex-wrap items-start gap-4 border-b border-[var(--color-line)] px-4 py-4 last:border-b-0">
      <img
        src={card.image.url}
        alt={"Bild zu Karte " + card.position}
        className="h-24 w-36 shrink-0 rounded-lg bg-[#f5f5f4] object-cover"
        loading="lazy"
      />
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-[var(--color-muted)]">Karte {card.position}</div>
        <h3 className="mt-0.5 mb-1 text-[17px] font-bold">{card.title}</h3>
        <p className="m-0 text-[15px] leading-relaxed text-[var(--color-ink)]">{card.body}</p>
        <p className="mt-2 mb-0 text-[12px] text-[var(--color-muted)]">{card.image.creditLine}</p>
      </div>
      <TButton onClick={onFlag} aria-label={"Karte " + card.position + " melden"}>
        Melden
      </TButton>
    </li>
  );
}

function QuestionRow({ question, onFlag }: { question: TopicQuestion; onFlag: () => void }) {
  return (
    <li className="flex flex-wrap items-start gap-4 border-b border-[var(--color-line)] px-4 py-4 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="text-[13px] text-[var(--color-muted)]">
          Frage {question.position} · {question.kind === "tf" ? "Richtig/Falsch" : "Auswahl"}
          {question.cardPosition !== null ? " · zu Karte " + question.cardPosition : ""}
        </div>
        <h3 className="mt-0.5 mb-2 text-[17px] font-bold">{question.prompt}</h3>
        <ul className="m-0 flex list-none flex-col gap-1 p-0">
          {question.options.map((option) => (
            <li key={option.id} className="text-[15px]">
              {option.isCorrect ? (
                <span className="font-semibold text-[var(--color-ok)]">✓ {option.text}</span>
              ) : (
                <span className="text-[var(--color-muted)]">○ {option.text}</span>
              )}
            </li>
          ))}
        </ul>
      </div>
      <TButton onClick={onFlag} aria-label={"Frage " + question.position + " melden"}>
        Melden
      </TButton>
    </li>
  );
}

function SourcePanel({ source }: { source: TopicDetail["source"] }) {
  const [open, setOpen] = useState(false);
  return (
    <Panel title="Quelle">
      <dl className="m-0 grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 px-4 py-4 text-[15px]">
        <dt className="text-[var(--color-muted)]">Seite</dt>
        <dd className="m-0">{source.site}</dd>
        <dt className="text-[var(--color-muted)]">Artikel</dt>
        <dd className="m-0">{source.title}</dd>
        <dt className="text-[var(--color-muted)]">Fassung</dt>
        <dd className="m-0">
          <ExternalLink href={source.url} className="text-[var(--color-accent)]">
            Version {source.revisionId}
          </ExternalLink>
        </dd>
        <dt className="text-[var(--color-muted)]">Abgerufen</dt>
        <dd className="m-0">{formatDate(source.retrievedAt)}</dd>
        <dt className="text-[var(--color-muted)]">Lizenz</dt>
        <dd className="m-0">
          <ExternalLink href={source.licenceUrl} className="text-[var(--color-accent)]">
            {source.licence}
          </ExternalLink>
        </dd>
      </dl>
      <div className="border-t border-[var(--color-line)] px-4 py-3">
        <TButton variant="quiet" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
          {open ? "Quelltext ausblenden" : "Ganzen Quelltext anzeigen"}
        </TButton>
        {open && (
          <pre className="mt-3 max-h-96 overflow-auto rounded-lg bg-[#f5f5f4] p-3 text-[13px] leading-relaxed whitespace-pre-wrap">
            {source.text}
          </pre>
        )}
      </div>
    </Panel>
  );
}

function FlagDialog({
  target,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  target: FlagTarget;
  pending: boolean;
  error: string | null;
  onSubmit: (reason: FlagReason, comment: string) => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState<FlagReason>("wrong");
  const [comment, setComment] = useState("");

  return (
    <Dialog title={target.label + " melden"} onClose={onClose}>
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(reason, comment);
        }}
      >
        <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
          <legend className="mb-1 p-0 text-[14px] font-semibold">Was stimmt nicht?</legend>
          {FLAG_REASONS.map((value) => (
            <label key={value} className="flex items-center gap-2 text-[15px]">
              <input
                type="radio"
                name="reason"
                value={value}
                checked={reason === value}
                onChange={() => setReason(value)}
              />
              {FLAG_REASON_LABELS[value]}
            </label>
          ))}
        </fieldset>

        <Field
          label="Kommentar (freiwillig)"
          hint={comment.length + " von " + MAX_COMMENT + " Zeichen"}
        >
          <textarea
            value={comment}
            maxLength={MAX_COMMENT}
            rows={4}
            onChange={(event) => setComment(event.target.value)}
            className={inputClass + " resize-y"}
          />
        </Field>

        {error && <ErrorLine>{error}</ErrorLine>}
        <div className="flex justify-end gap-2">
          <TButton onClick={onClose}>Abbrechen</TButton>
          <TButton type="submit" variant="primary" disabled={pending}>
            {pending ? "…" : "Senden"}
          </TButton>
        </div>
      </form>
    </Dialog>
  );
}
